import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import Spinner from "presentation/components/Shared/Spinner";
import { useEquipmentsByFilter } from "presentation/hooks/use-query-equipments";
import { useEquipmentTypeByFilterAll } from "presentation/hooks/use-query-equipment-type";
import { Equipment } from "domain/entities/equipment";

interface EmployeeEquipmentsTabProps {
  employeeId?: string;
}

const EmployeeEquipmentsTab: React.FC<EmployeeEquipmentsTabProps> = ({
  employeeId,
}) => {
  // Buscar todos os tipos de equipamentos
  const { data: eqTypesResponse } = useEquipmentTypeByFilterAll({
    limit: 100,
  } as any);

  // Buscar equipamentos alocados ao funcionário
  const { data: response, isLoading } = useEquipmentsByFilter(
    {
      employeeId,
      limit: 100,
    },
    !!employeeId,
  );

  const statusLabelMap: Record<string, string> = {
    IN_USE: "Em uso",
    IN_MAINTENANCE: "Em manutenção",
    IN_STOCK: "Em stock",
    LOST: "Perdido",
    OBSOLETE: "Obsoleto",
  };

  const statusBodyTemplate = (rowData: Equipment) => {
    const status = rowData.status;
    const label = statusLabelMap[status] || status;
    let customStyle: React.CSSProperties = {
      fontWeight: "600",
      fontSize: "0.85rem",
      padding: "0.25rem 0.6rem",
    };

    if (status === "IN_USE") {
      customStyle = {
        ...customStyle,
        backgroundColor: "#eafaf1",
        color: "#27ae60",
        border: "1px solid #c2f0d1",
      };
    } else if (status === "IN_MAINTENANCE") {
      customStyle = {
        ...customStyle,
        backgroundColor: "#fff3e6",
        color: "#c26000",
        border: "1px solid #ffe0b2",
      };
    } else if (status === "IN_STOCK") {
      customStyle = {
        ...customStyle,
        backgroundColor: "#ebf5fe",
        color: "#1e40af",
        border: "1px solid #bfdbfe",
      };
    } else if (status === "LOST") {
      customStyle = {
        ...customStyle,
        backgroundColor: "#fdf2f2",
        color: "#c81e1e",
        border: "1px solid #fde8e8",
      };
    } else if (status === "OBSOLETE") {
      customStyle = {
        ...customStyle,
        backgroundColor: "#f3f4f6",
        color: "#374151",
        border: "1px solid #e5e7eb",
      };
    }

    return <Tag value={label} rounded style={customStyle} />;
  };

  const equipmentTypeBodyTemplate = (rowData: Equipment) => {
    const eqType = eqTypesResponse?.data?.find(
      (t) => t.id === rowData.equipmentTypeId,
    );
    return eqType?.name || rowData.equipmentTypeId;
  };

  if (isLoading) return <Spinner />;

  if (!employeeId) {
    return (
      <div className="text-center text-gray-500 py-4">
        Guarde o colaborador primeiro para gerir equipamentos alocados.
      </div>
    );
  }

  return (
    <div>
      <DataTable
        value={response?.data?.data || []}
        emptyMessage="Nenhum equipamento alocado a este colaborador."
        paginator
        rows={10}
        size="small"
      >
        <Column
          field="equipmentTypeId"
          header="Tipo de Equipamento"
          body={equipmentTypeBodyTemplate}
          sortable
        />
        <Column field="brand" header="Marca" sortable />
        <Column field="model" header="Modelo" sortable />
        <Column
          field="serialOrCarRegistration"
          header="Matrícula/Nº Série"
          sortable
        />
        <Column
          field="status"
          header="Status"
          body={statusBodyTemplate}
          sortable
        />
      </DataTable>
    </div>
  );
};

export default EmployeeEquipmentsTab;
